import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { WriteOffStatus } from '@repo/types';
import { useTableParams } from './useTableParams';
import { INITIAL_FILTERS, DEFAULT_WRITE_OFF_SUBTAB, WRITE_OFF_SUBTAB_TO_STATUS } from '../constants';

/**
 * Extended table params hook for Write-Offs that manages subtab in URL.
 * Wraps useTableParams and adds subtab state without modifying the base hook.
 *
 * Returns:
 * - tableParams, setParams, resetParams: From base useTableParams
 * - subtab: Current subtab from URL (defaults to 'proposed')
 * - setSubtab: Function to change subtab (resets page to 1)
 * - currentStatus: WriteOffStatus based on current subtab
 */
export const useWriteOffTableParams = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { tableParams, setParams, resetParams } = useTableParams({
        page: 1,
        limit: 25,
        filters: INITIAL_FILTERS,
    });

    // Get subtab from URL or default
    const subtab = searchParams.get('subtab') || DEFAULT_WRITE_OFF_SUBTAB;
    const currentStatus = WRITE_OFF_SUBTAB_TO_STATUS[subtab] || WriteOffStatus.PENDING;

    // Set subtab and reset page to 1, remove entityId from filters when switching tabs
    const setSubtab = useCallback(
        (value: string) => {
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev);
                    next.set('subtab', value);
                    next.set('page', '1');
                    
                    // Remove entityId from filters when switching tabs
                    // This ensures we go back to grouped clients view instead of staying in invoice detail view
                    try {
                        const filtersParam = next.get('filters');
                        if (filtersParam) {
                            const filters = JSON.parse(filtersParam);
                            if (filters.entityId) {
                                delete filters.entityId;
                                next.set('filters', JSON.stringify(filters));
                            }
                        }
                    } catch {
                        // Ignore JSON parse errors
                    }
                    
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    // Initialize default subtab if missing
    useEffect(() => {
        if (!searchParams.has('subtab')) {
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev);
                    next.set('subtab', DEFAULT_WRITE_OFF_SUBTAB);
                    return next;
                },
                { replace: true }
            );
        }
    }, [searchParams, setSearchParams]);

    return {
        tableParams,
        setParams,
        resetParams,
        subtab,
        setSubtab,
        currentStatus,
    };
};
